import { Plus } from 'lucide-react';
import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { Flex, VFlex } from '@/components/base';
import Button from '@/components/Button/Button';
import Modal from '@/components/Modal/Modal';
import useMaxTaxonomyCount from '@/configs/useMaxTaxonomyCount';
import TaxonomyLimitGraph from '@/features/Taxonomy/components/TaxonomyList/TaxonomyLimitGraph';
import TaxonomyList from '@/features/Taxonomy/components/TaxonomyList/TaxonomyList';
import useTaxonomyListQuery from '@/features/Taxonomy/components/TaxonomyList/useTaxonomyList';
import TaxonomySetupModal from '@/features/Taxonomy/components/TaxonomySetupModal/TaxonomySetupModal';
import useModalState from '@/hooks/pure/useModalState';
import { PageHeader } from '@/shared/ui';

const TaxonomyListPage = () => {
  const location = useLocation();
  const { maxTaxonomyCount } = useMaxTaxonomyCount();

  const { data, isFetching } = useTaxonomyListQuery();
  const createModalState = useModalState();
  const editModalState = useModalState({
    alias: '',
  });

  const onCreateClick = useCallback(() => {
    createModalState.open();
  }, [createModalState]);

  const onEditClick = useCallback((alias: string) => {
    editModalState.open({
      modalState: {
        alias,
      },
    });
  }, [editModalState]);

  const sortedData = useMemo(() => {
    return data?.sort((a, b) => Number(b.starred) - Number(a.starred));
  }, [data]);
  const visibleTaxonomyCount = sortedData?.length || 0;
  const isSettingsRoute = location.pathname.includes('/settings/');

  return (
    <Style $isSettingsRoute={isSettingsRoute}>
      <PageHeader
        title="Taxonomy"
        desc="Let AI classify your data."
        topRightNode={
          <Flex a="center" g={24}>
            <TaxonomyLimitGraph current={visibleTaxonomyCount} />
            <Button
              sizeType="l"
              leftIcon={<Plus />}
              variant="primary"
              onClick={onCreateClick}
              disabled={visibleTaxonomyCount >= maxTaxonomyCount}
            >
              Taxonomy
            </Button>
          </Flex>
        }
      />
      <TaxonomyList
        taxonomyList={sortedData}
        isFetching={isFetching}
        onCreateClick={onCreateClick}
        onEditClick={onEditClick}
      />
      <Modal
        status={createModalState.status}
        closeModal={createModalState.close}
      >
        <TaxonomySetupModal mode="CREATE" />
      </Modal>
      <Modal status={editModalState.status} closeModal={editModalState.close}>
        <TaxonomySetupModal
          taxonomyAlias={editModalState.modalState.alias}
          mode="EDIT"
        />
      </Modal>
    </Style>
  );
};

const Style = styled(VFlex)<{ $isSettingsRoute: boolean }>`
  height: ${({ $isSettingsRoute }) => ($isSettingsRoute ? '100%' : '100vh')};
  overflow: auto;
`;

export default TaxonomyListPage;
